// components/Loading.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The one "this is working" indicator.
//
// REPORTED 27 Sep 2026: "the loading indicator is not visible, I've noticed
// this pattern in a number of places too."
//
// It was not one place. `<Spinner animation="border" />` renders in
// `currentColor`, and this codebase has 27 bare one-line spinners plus many
// more across several lines, each inheriting whatever text colour the panel
// around it happened to set. Inside a light wrapper that is a near-white ring
// on a near-white background, and a page that is loading looks exactly like a
// page that has crashed. Two more loaders existed already —
// views/Components/Loader.js (used once) and views/Components/DotLoader.js —
// each with its own size and colour, so there were three answers to one
// question and none of them was reliably visible.
//
// This is the answer. Three sizes for the three real cases, the action navy
// on a faint track so the ring reads as a ring at every frame, and a LABEL,
// because a spinner alone never says whether it is loading or stuck.
//
// The colours and the track live in assets/css/lusuku-overrides.css under
// `.lk-loading`, next to the global spinner floor, so nothing here hard-codes
// a hex.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { Spinner } from "react-bootstrap";

const DIAMETER = {
  page: 46,
  panel: 34,
  inline: 18,
};

/**
 * @param {"page"|"panel"|"inline"} [props.size]
 *        page   — a route that has nothing to show yet
 *        panel  — one card or table inside a page that already rendered
 *        inline — beside a label, inside a button, in a table cell
 * @param {string} [props.label]  what is being waited for. Defaults to
 *                                "Loading...". Pass null to show no text.
 * @param {object} [props.style]
 * @param {string} [props.className]
 */
const Loading = ({ size = "panel", label = "Loading...", style, className }) => {
  const diameter = DIAMETER[size] || DIAMETER.panel;

  return (
    <div
      className={["lk-loading", `lk-loading--${size}`, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role="status"
      aria-live="polite"
    >
      <Spinner
        animation="border"
        style={{ width: diameter, height: diameter }}
        aria-hidden="true"
      />
      {label ? <span>{label}</span> : <span className="sr-only">Loading</span>}
    </div>
  );
};

export default Loading;
